import React from 'react'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import ToolTip from './tooltip'

const Target = glamorous.button({
  fontSize: '24px',
  position: 'relative',
  color: '#0070ba',
  cursor: 'pointer',
  zIndex: '1',
  border: 'none',
})

const TooltipContainer = glamorous.span(
  {
    position: 'relative',
    display: 'inline-block',
    textAlign: 'center',
  },
  ({ isDebug }) => ({
    border: isDebug ? '1px dotted magenta' : 'unset', // for easier debugging of bounding tooltip targe
  })
)

const verticalPositionOptions = ['top', 'bottom']
const horizontalPositionOptions = ['left', 'middle', 'right']

class TooltipTarget extends React.PureComponent {
  displayName: 'vx-tool-tip'

  static propTypes = {
    isDebug: PropTypes.bool,
    dataTestId: PropTypes.string,
    content: PropTypes.string.isRequired,
    isClickable: PropTypes.bool,
    handleClick: PropTypes.func,
    isHoverable: PropTypes.bool,
    handleHover: PropTypes.func,
    verticalPosition: PropTypes.string,
    horizontalPosition: PropTypes.string,
    iconClasses: PropTypes.string,
    children: PropTypes.element,
    className: PropTypes.string,
    renderTarget: PropTypes.func,
  }

  state = {
    showTooltipBubble: false,
  }

  static defaultProps = {
    verticalPosition: 'top',
    horizontalPosition: 'middle',
    content: 'looks like you forgot some content =(',
    isClickable: false,
    isHoverable: false,
    iconClasses: 'icon icon-tooltip-small',
    isDebug: false,
    dataTestId: 'tooltip',
    renderTarget: ([children, targetProps]) => (
      <Target {...targetProps}>{children}</Target>
    ),
  }

  showTooltip = () => {
    this.setState({
      showTooltipBubble: !this.state.showTooltipBubble,
    })
  }

  hoverIn = event => {
    event.preventDefault()
    clearTimeout(this.blurTimer)
    this.handleHover(event) // called here for analytics or anything else
    this.setState({ showTooltipBubble: true })
  }

  hoverOut = event => {
    this.blurTimer = setTimeout(() => {
      this.setState({ showTooltipBubble: false })
    }, 200)
  }

  // if supplied handleClick can be used to call another function such as analytics specific to that tool tip
  handleClick = event => {
    event.preventDefault()

    const { isClickable, isHoverable } = this.props

    // if the target is both clickable AND hoverable then for MOBILE we do not want to show the tooltip
    // the touchEnd event should show the tooltip ONLY if the target is clickable ONLY and NOT hoverable
    if (!(isClickable && isHoverable && event.type === 'touchend')) {
      this.showTooltip()
    }

    if (this.props.handleClick) {
      this.props.handleClick()
    }
  }

  // if supplied handleHover can be used to call another function such as analytics specific to that tool tip
  handleHover = event => {
    event.preventDefault()
    if (this.props.handleHover) {
      this.props.handleHover()
    }
  }

  _renderTooltipTarget = () => {
    const targetProps = {
      ['data-testid']: this.props.dataTestId,
    }

    // We can use the vx-icons if supplied or else it will use the
    // default 'icon icon-tooltip-small'
    if (React.Children.count(this.props.children) === 0) {
      Object.assign(targetProps, {
        className: this.props.iconClasses,
      })
    }

    // apply hoverable if true
    if (this.props.isHoverable) {
      Object.assign(targetProps, {
        onMouseEnter: this.hoverIn,
        onMouseLeave: this.hoverOut,
      })
    }

    // apply hoverable if true
    if (this.props.isClickable) {
      Object.assign(targetProps, {
        onClick: this.handleClick,
        onTouchEnd: this.handleClick,
      })
    }

    return this.props.renderTarget([this.props.children, targetProps])
  }

  _renderBubble = () => {
    const { verticalPosition, horizontalPosition, content } = this.props
    const vertical = verticalPositionOptions.includes(verticalPosition)
      ? verticalPosition
      : 'top'
    const horizontal = horizontalPositionOptions.includes(horizontalPosition)
      ? horizontalPosition
      : 'left'

    return (
      <ToolTip
        verticalPosition={vertical}
        horizontalPosition={horizontal}
        content={content}
        isVisible={this.state.showTooltipBubble}
      />
    )
  }

  render() {
    return (
      <TooltipContainer isDebug={this.props.isDebug}>
        {this._renderTooltipTarget()}
        {this._renderBubble()}
      </TooltipContainer>
    )
  }
}

export default TooltipTarget
